import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';

interface LocationFAQProps {
  cityName: string;
  countyName: string;
  population: number;
  isCoastal?: boolean;
}

const LocationFAQ: React.FC<LocationFAQProps> = ({ 
  cityName, 
  countyName, 
  population,
  isCoastal = false 
}) => {
  
  const generateLocationFAQs = () => {
    const baseFAQs = [
      {
        question: `How much does roof replacement cost in ${cityName}?`,
        answer: `Roof replacement costs in ${cityName} typically range from $8,000 to $25,000 depending on your home's size, chosen materials, and project complexity. ${countyName} County permit fees add $75-$300 to total costs. We provide detailed, written estimates with no hidden fees and offer flexible financing options for ${cityName} residents.`
      },
      {
        question: `Do I need a permit for roofing work in ${cityName}?`,
        answer: `Yes, most roofing projects in ${cityName} require permits from ${countyName} County. Our team handles all permit applications and coordinates required inspections, ensuring your project meets current North Carolina Building Code requirements. This service is included in our project management at no extra cost.`
      },
      {
        question: `How long does roof installation take in ${cityName}?`,
        answer: `Most residential roof replacements in ${cityName} take 1-3 days depending on weather, roof size, and material complexity. We schedule around ${cityName}'s weather patterns and provide detailed timelines before starting. Emergency repairs can often be completed the same day.`
      },
      {
        question: `What roofing materials work best in ${cityName}?`,
        answer: `For ${cityName}'s climate, we recommend architectural shingles, metal roofing, or tile systems depending on your budget and style preferences. ${isCoastal ? 'Coastal locations benefit from impact-resistant materials and enhanced corrosion protection.' : 'Your location allows for a full range of premium roofing materials.'} We'll assess your specific needs during our free consultation.`
      },
      {
        question: `Does Carolina Trophy Roofs serve all of ${cityName}?`,
        answer: `Yes! We proudly serve all areas of ${cityName} and throughout ${countyName} County. ${population > 50000 ? 'Our local crews ensure fast response times throughout the city.' : 'We coordinate our services efficiently to serve smaller communities with the same quality as larger cities.'} Call us at (252) 302-2003 to confirm service to your specific address.`
      }
    ];

    // Add location-specific FAQs
    const locationSpecificFAQs = [];
    
    if (isCoastal) {
      locationSpecificFAQs.push({
        question: `How do you protect against hurricane damage in ${cityName}?`,
        answer: `${cityName}'s coastal location requires specialized hurricane preparation. We install impact-resistant materials, enhanced fastening systems, and proper drainage designed for high winds and heavy rainfall. Our installations meet or exceed coastal building codes for wind resistance.`
      });
    }

    if (population < 10000) {
      locationSpecificFAQs.push({
        question: `Do you really service smaller communities like ${cityName}?`,
        answer: `Absolutely! ${cityName} residents deserve the same premium quality as larger cities. We coordinate our services throughout ${countyName} County efficiently, often grouping nearby projects to provide cost savings while maintaining our high standards and full warranty coverage.`
      });
    }

    return [...baseFAQs, ...locationSpecificFAQs];
  };

  const faqs = generateLocationFAQs();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-brand-trophy-gold" />
          Frequently Asked Questions - {cityName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left font-semibold">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        
        {/* Contact CTA within FAQ */}
        <div className="mt-8 p-6 bg-brand-trophy-gold/10 rounded-lg text-center">
          <h4 className="font-semibold text-brand-charcoal-gray mb-2">
            Have More Questions About Roofing in {cityName}?
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            Our local experts are ready to provide personalized answers for your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="tel:2523022003"
              className="bg-brand-safety-orange text-white px-4 py-2 rounded-lg font-medium hover:bg-brand-safety-orange/90 transition-colors"
            >
              Call (252) 302-2003
            </a>
            <a 
              href="/book-inspection"
              className="bg-brand-trophy-gold text-brand-charcoal-gray px-4 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              Schedule Free Consultation
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationFAQ;